import { useState, useCallback } from 'react';
import { parseStudentNames } from '../utils/calendarColors';

export function useCalendar(accessToken) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 從單一日曆抓指定月份事件
  async function fetchOneCalendar(calendarId, year, month, token) {
    const timeMin = new Date(year, month - 1, 1).toISOString();
    const timeMax = new Date(year, month, 0, 23, 59, 59).toISOString();
    const res = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events` +
      `?timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=true&orderBy=startTime&maxResults=500`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!res.ok) return [];
    const data = await res.json();
    // 每筆事件標記來源日曆 ID
    return (data.items || []).map(ev => ({ ...ev, _calendarId: calendarId }));
  }

  const fetchEvents = useCallback(async (year, month) => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      // 讀取啟用的日曆清單
      let calendarIds = [];
      try {
        const saved = localStorage.getItem('enabledCalendars');
        if (saved) {
          const enabled = JSON.parse(saved);
          calendarIds = Object.entries(enabled).filter(([,v]) => v).map(([id]) => id);
        }
      } catch {}

      // 沒有設定就用 .env 的預設
      if (calendarIds.length === 0) {
        calendarIds = [process.env.REACT_APP_CALENDAR_ID].filter(Boolean);
      }

      // 並行抓取所有啟用日曆
      const results = await Promise.all(
        calendarIds.map(id => fetchOneCalendar(id, year, month, accessToken))
      );
      const allRaw = results.flat();
      const parsed = parseEvents(allRaw);
      // 合併事件：保留其他月份已有的事件，只替換本次抓取的月份
      const fetchedDates = new Set(parsed.map(ev => {
        const p = ev.date.split('/');
        return `${p[0]}-${p[1]}`;  // YYYY-M
      }));
      setEvents(prev => {
        // 移除本次抓取月份的舊事件，加入新事件
        const kept = prev.filter(ev => {
          const p = ev.date.split('/');
          return !fetchedDates.has(`${p[0]}-${p[1]}`);
        });
        return [...kept, ...parsed];
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  return { events, loading, error, fetchEvents };
}

function parseEvents(rawEvents) {
  const result = [];
  rawEvents.forEach(ev => {
    const title = ev.summary || '';
    const colorId = ev.colorId || null;
    const calendarId = ev._calendarId || null;

    const startDT = ev.start?.dateTime || ev.start?.date || '';
    const endDT = ev.end?.dateTime || ev.end?.date || '';
    const startDate = startDT ? new Date(startDT) : null;
    const endDate = endDT ? new Date(endDT) : null;

    const dateStr = startDate
      ? `${startDate.getFullYear()}/${startDate.getMonth() + 1}/${startDate.getDate()}`
      : '';
    const timeStr = startDate && ev.start?.dateTime
      ? `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`
      : '';
    const endTimeStr = endDate && ev.end?.dateTime
      ? `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`
      : '';

    const studentNames = parseStudentNames(title);
    studentNames.forEach(name => {
      result.push({
        id: `${ev.id}_${name}`,
        rawId: ev.id,
        studentName: name,
        colorId,
        calendarId,
        date: dateStr,
        time: timeStr,
        endTime: endTimeStr,
        startDT,
        isTrial: false,
        notes: ev.description || '',
      });
    });
  });
  return result;
}

export function countLessonsForStudent(events, studentName, excludeTrial = true) {
  return events.filter(ev => {
    if (ev.studentName !== studentName) return false;
    if (excludeTrial && ev.isTrial) return false;
    return true;
  }).length;
}
